import { useEffect, useState } from "react";
import { api, fileUrl, money } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowRight, Sparkles } from "lucide-react";

let uidc = 1000;

export default function Builder() {
  const { categories, builderCategories } = useSettings();
  const navigate = useNavigate();
  const [styles, setStyles] = useState([]);
  const [activeCat, setActiveCat] = useState("");
  const [selected, setSelected] = useState([]); // {uid, style...}

  useEffect(() => {
    api.get("/styles").then((r) => setStyles(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (builderCategories.length && !builderCategories.some((c) => c.id === activeCat)) setActiveCat(builderCategories[0].id);
  }, [builderCategories]); // eslint-disable-line

  const catName = (id) => categories.find((c) => c.id === id)?.name || "";
  const inSet = (styleId) => selected.some((s) => s.style_id === styleId);

  const toggle = (s) => {
    if (inSet(s.id)) {
      setSelected((prev) => prev.filter((x) => x.style_id !== s.id));
    } else {
      setSelected((prev) => [...prev, {
        uid: uidc++, style_id: s.id, style_name: s.name, category_id: s.category_id,
        category_name: catName(s.category_id), style_image: s.images?.[0] || null,
        base_price: s.base_price ?? null,
        allow_rhinestones: s.allow_rhinestones, allow_ruffles: s.allow_ruffles, allow_hardware: s.allow_hardware,
        fabric_id: null, fabric_name: null, fabric_image: null, addon_keys: [], measurements: {}, notes: "",
      }]);
      toast.success(`${s.name} added to your set`);
    }
  };

  const proceed = () => {
    if (!selected.length) { toast.error("Add at least one piece to your set"); return; }
    navigate("/custom-order", { state: { pieces: selected } });
  };

  const list = styles.filter((s) => s.category_id === activeCat);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="text-center">
        <p className="label-accent text-kys-accent">Mix & Match</p>
        <h1 className="mt-2 font-serif text-4xl tracking-tight text-kys-ink sm:text-5xl">Build Your Set</h1>
        <p className="mx-auto mt-3 max-w-xl text-sm text-kys-charcoal">Combine Kys Boutique designs into a one-of-a-kind look. Pick a top, a bottom, a skirt — or just a bodysuit. When you're ready, continue to configure fabrics and send it as a custom quote.</p>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12">
        {/* Selection area */}
        <div className="lg:col-span-8">
          <div className="no-scrollbar flex gap-2 overflow-x-auto pb-2">
            {builderCategories.map((c) => (
              <button key={c.id} data-testid={`builder-cat-${c.slug}`} onClick={() => setActiveCat(c.id)}
                className={`whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition ${activeCat === c.id ? "border-kys-ink bg-kys-ink text-white" : "border-kys-blush-border bg-white text-kys-charcoal hover:border-kys-accent"}`}>
                {c.name}
              </button>
            ))}
          </div>
          {list.length === 0 ? (
            <p className="mt-8 text-sm text-kys-muted">No designs in this category yet.</p>
          ) : (
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {list.map((s) => {
                const on = inSet(s.id);
                return (
                  <button key={s.id} data-testid={`builder-style-${s.name.toLowerCase()}`} onClick={() => toggle(s)}
                    className={`group relative overflow-hidden rounded-md border text-left transition ${on ? "border-kys-accent ring-2 ring-kys-accent/40" : "border-kys-blush-border hover:border-kys-accent"}`}>
                    <div className="aspect-[3/4] w-full overflow-hidden bg-kys-blush">
                      {s.images?.[0] ? <img src={fileUrl(s.images[0])} alt={s.name} className="h-full w-full object-cover" /> :
                        <div className="flex h-full w-full items-center justify-center text-xs text-kys-muted">No photo</div>}
                    </div>
                    {on && <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-kys-accent text-white"><Check className="h-4 w-4" /></span>}
                    <div className="p-2">
                      <p className="text-sm font-medium text-kys-ink">{s.name}</p>
                      <p className="text-xs text-kys-muted">{s.base_price != null ? `from ${money(s.base_price)}` : "Priced on request"}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Set summary */}
        <div className="lg:col-span-4">
          <div className="sticky top-20 rounded-lg border border-kys-blush-border bg-white p-5">
            <h2 className="flex items-center gap-2 font-serif text-2xl text-kys-ink"><Sparkles className="h-5 w-5 text-kys-accent" /> Your Set</h2>
            {selected.length === 0 ? (
              <p className="mt-4 text-sm text-kys-muted">No pieces yet. Tap designs to add them to your set.</p>
            ) : (
              <div className="mt-4 space-y-3">
                {selected.map((p) => (
                  <div key={p.uid} className="flex items-center gap-3">
                    <div className="h-14 w-12 shrink-0 overflow-hidden rounded bg-kys-blush">
                      {p.style_image && <img src={fileUrl(p.style_image)} alt={p.style_name} className="h-full w-full object-cover" />}
                    </div>
                    <div className="flex-1 text-sm">
                      <p className="font-medium text-kys-ink">{p.style_name}</p>
                      <p className="text-xs text-kys-muted">{p.category_name} · {p.base_price != null ? money(p.base_price) : "Priced on request"}</p>
                    </div>
                    <button onClick={() => setSelected((prev) => prev.filter((x) => x.uid !== p.uid))} className="text-xs text-kys-muted hover:text-destructive">Remove</button>
                  </div>
                ))}
              </div>
            )}
            {selected.length > 0 && (
              <div className="mt-4 flex items-center justify-between border-t border-kys-blush-border pt-3">
                <span className="text-sm font-medium text-kys-ink">Estimated starting total</span>
                <span className="font-mono text-kys-ink">{money(selected.reduce((s, p) => s + (p.base_price || 0), 0))}{selected.some((p) => p.base_price == null) ? "+" : ""}</span>
              </div>
            )}
            <button data-testid="builder-continue" onClick={proceed}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-kys-ink py-3.5 text-sm font-semibold text-white transition hover:bg-kys-accent">
              Continue to Customize <ArrowRight className="h-4 w-4" />
            </button>
            <p className="mt-3 text-center text-xs text-kys-muted">Next: choose fabrics & add-ons per piece, then request a quote.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
